/**
 * صفحه‌ی کشفِ شهری که هنوز هیچ محتوایی ندارد (WHOSUP-283).
 *
 * فهرستِ خالی دو معنی دارد: یا فیلتر/جستجو چیزی باقی نگذاشته، یا این شهر
 * اصلاً هنوز چیزی ندارد. دومی دیگر «هیچی نیست» نمی‌گوید: کاربر را
 * «پیشگامِ» شهرش می‌خواند، می‌گوید جمع‌آوری شروع شده، و دو کار جلویش
 * می‌گذارد (دعوتِ دوستان، ساختنِ برنامه) و — اگر شهرِ نزدیکی محتوا دارد —
 * پیشنهادِ دیدنِ همان.
 *
 * ⚠ متن عمداً «به‌زودی» می‌گوید نه «از فردا»: کرالر هر شب فقط **یک** شهرِ
 * تازه را می‌گردد (crawler_cities_needing_discovery)، پس «فردا» قولی است
 * که همیشه نمی‌شود سرش ماند. و «جزو پیشگامان» نه «اولین نفر»: نبودنِ
 * رخداد یعنی منبع نداریم، نه اینکه کاربرِ دیگری از این شهر نداریم.
 *
 * بی‌import از supabase تا تستِ node بارش کند — همان دلیلِ nearest.ts.
 */
import { distanceKm } from './nearest.ts';
import type { AppCity } from '../domain/types';

/**
 * دورتر از این «شهرِ نزدیک» نیست. ۳۰۰ کیلومتر یعنی رفت‌وبرگشتِ یک روزه؛
 * پیشنهادِ شهری در قاره‌ی دیگر بیشتر شبیهِ خطاست تا کمک.
 */
export const NEARBY_MAX_KM = 300;

export type EmptyKind = 'filtered' | 'pioneer';

/**
 * کدام پیامِ خالی. `cityHasContent` یعنی شهر **قبل از** هر فیلتری چیزی
 * دارد؛ اگر دارد و باز خالی است، تقصیرِ فیلتر است نه شهر.
 */
export function emptyKind(o: { filtering: boolean; cityHasContent: boolean }): EmptyKind {
  return o.filtering || o.cityHasContent ? 'filtered' : 'pioneer';
}

/**
 * نزدیک‌ترین شهرِ دارای محتوا به `city`، اگر به‌قدرِ کافی نزدیک باشد.
 *
 * `withContent` همان `cityRepo.list` است که فقط شهرهای دارای رخداد را
 * می‌دهد. خودِ شهر کنار گذاشته می‌شود، و شهرِ بی‌مختصات هم (برای هر دو
 * طرف) — «نمی‌دانم کجاست» با «همین‌جاست» یکی نیست.
 */
export function nearbyCity(
  city: AppCity | null, withContent: readonly AppCity[], maxKm = NEARBY_MAX_KM,
): AppCity | null {
  if (!city || typeof city.lat !== 'number' || typeof city.lng !== 'number') return null;
  let best: AppCity | null = null;
  let bestKm = Infinity;
  for (const c of withContent) {
    if (c.id === city.id) continue;
    if (typeof c.lat !== 'number' || typeof c.lng !== 'number') continue;
    const km = distanceKm(city.lat, city.lng, c.lat, c.lng);
    if (km < bestKm) { bestKm = km; best = c; }
  }
  return bestKm <= maxKm ? best : null;
}
